/**
 * The "how many points is this worth" promise (UX-SPEC §5 "Live preview"):
 * the formed words as tile-lettered chips with per-word points, ✓/✗ from the
 * dictionary (a neutral dot while checking), the multiplier breakdown in a
 * tooltip, a bingo badge and a big animated total. Mobile gets a one-line
 * strip instead ("NO 3, NA 3, OM 4 · 10 pts"), which opens the same card as
 * a sheet with each breakdown written out (no tooltips on touch); both feed a
 * throttled polite live region.
 *
 * The preview is the one place a refused move is explained: Enter or the
 * disabled Play on an invalid move rings the squares and bumps
 * draftStore.shakeNonce, and the preview shakes and scrolls into view.
 */
import { useEffect, useId, useRef, useState } from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { BINGO_BONUS } from '@wordloom/engine';
import { previewLine, previewParts, wordBreakdown, type PreviewModel, type PreviewWord } from '../game/preview';
import { useReducedMotion } from '../hooks/useMediaQuery';
import { useDraftStore } from '../state/draftStore';
import { useUiStore } from '../state/uiStore';
import { AnimatedNumber } from './AnimatedNumber';
import { IconCheck, IconChevronUp, IconSparkle, IconX } from './Icons';
import { Tooltip } from './Tooltip';
import './MovePreview.css';

interface MovePreviewProps {
  preview: PreviewModel;
  /** No tiles on the board yet: remind about the ★. */
  firstMove: boolean;
  isMyTurn: boolean;
  /** The phone's sheet: each word's breakdown written out instead of in a tooltip. */
  detailed?: boolean;
}

const SHAKE: Keyframe[] = [
  { transform: 'translateX(0)' },
  { transform: 'translateX(-5px)' },
  { transform: 'translateX(5px)' },
  { transform: 'translateX(-3px)' },
  { transform: 'translateX(3px)' },
  { transform: 'translateX(0)' },
];

/**
 * A refused move (draftStore.shakeNonce went up): shake the element that says
 * why and bring it on screen if it is not (the preview sits below the fold on
 * a portrait tablet). No toast repeats it, so it can't outlive the draft.
 */
function useRefusalNudge(target: () => HTMLElement | null, enabled = true): void {
  const nonce = useDraftStore((s) => s.shakeNonce);
  const reduced = useReducedMotion();
  const seen = useRef(nonce);
  useEffect(() => {
    if (nonce === seen.current) return;
    seen.current = nonce;
    const el = enabled ? target() : null;
    if (!el) return;
    el.scrollIntoView?.({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
    if (!reduced) el.animate?.(SHAKE, { duration: 320, easing: 'ease-in-out' });
  }, [nonce, enabled, reduced, target]);
}

function statusLabel(w: PreviewWord): string {
  if (w.status === 'valid') return 'valid word';
  if (w.status === 'invalid') return 'not in the dictionary';
  return 'checking';
}

function WordChip({ word, detailed = false }: { word: PreviewWord; detailed?: boolean }) {
  const chip = (
    <span className="word-chip__inner" tabIndex={detailed ? undefined : 0}>
      <span className="sr-only">{`${word.word}, ${word.score} points, ${statusLabel(word)}`}</span>
      <span className="word-chip__tiles" aria-hidden="true">
        {word.cells.map((c, i) => (
          <span key={i} className={clsx('tile word-chip__tile', c.isNew && 'is-new')} data-blank={c.blank || undefined}>
            <span className="tile__letter">{c.letter}</span>
          </span>
        ))}
      </span>
      <span className="word-chip__score tabular" aria-hidden="true">
        {word.score}
      </span>
      <span className="word-chip__status" aria-hidden="true">
        {word.status === 'valid' ? <IconCheck /> : word.status === 'invalid' ? <IconX /> : <span className="word-chip__dot" />}
      </span>
    </span>
  );
  return (
    <li className={clsx('word-chip', `word-chip--${word.status}`, detailed && 'word-chip--detailed')}>
      {detailed ? (
        <>
          {chip}
          <span className="word-chip__why tabular">{wordBreakdown(word)}</span>
        </>
      ) : (
        <Tooltip content={wordBreakdown(word)}>{chip}</Tooltip>
      )}
    </li>
  );
}

export function MovePreview({ preview, firstMove, isMyTurn, detailed = false }: MovePreviewProps) {
  const empty = preview.state === 'empty';
  const cardRef = useRef<HTMLElement>(null);
  const reasonRef = useRef<HTMLParagraphElement>(null);
  // The reason when there is one (it says why), else the whole card.
  useRefusalNudge(() => reasonRef.current ?? cardRef.current, !detailed);
  return (
    <section
      ref={cardRef}
      className={clsx('preview', detailed ? 'preview--detailed' : 'card')}
      aria-labelledby={detailed ? undefined : 'preview-title'}
      data-coach={detailed ? undefined : 'preview'}
    >
      <header className="preview__head">
        {/* In the sheet the dialog's title says "This move". */}
        {detailed ? (
          <span className="eyebrow">Total</span>
        ) : (
          <h2 id="preview-title" className="eyebrow">
            This move
          </h2>
        )}
        {!empty && preview.total != null && (
          <span className={clsx('preview__total', preview.state === 'invalid' && 'is-invalid')}>
            <AnimatedNumber value={preview.total} />
            <span className="preview__pts">pts</span>
          </span>
        )}
      </header>

      {empty ? (
        <p className="preview__empty">
          Place tiles to see your score.
          {firstMove && (
            <>
              {' '}
              First word must cover the <span className="preview__star">★</span>.
            </>
          )}
          {!isMyTurn && <span className="preview__hint"> You can arrange tiles while you wait.</span>}
        </p>
      ) : (
        <>
          {preview.words.length > 0 && (
            <ul className="preview__words" aria-label="Words formed">
              {preview.words.map((w, i) => (
                <WordChip key={`${w.word}-${w.direction}-${i}`} word={w} detailed={detailed} />
              ))}
            </ul>
          )}
          <AnimatePresence initial={false}>
            {preview.bingo && (
              <motion.p
                className="preview__bingo"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
              >
                <IconSparkle /> Bingo! +{BINGO_BONUS}
              </motion.p>
            )}
          </AnimatePresence>
          {preview.state === 'invalid' && preview.reason && (
            <p
              ref={reasonRef}
              className={clsx('preview__reason', preview.tone === 'info' && 'preview__reason--info')}
              role="note"
            >
              {preview.tone !== 'info' && <IconX />} {preview.reason}
            </p>
          )}
          {preview.state === 'valid' && !isMyTurn && (
            <p className="preview__hint">Ready — play it when your turn comes.</p>
          )}
        </>
      )}
    </section>
  );
}

/**
 * Mobile: one line under the board ("NO 3, NA 3, OM 4 · 10 pts"; the total
 * never ellipsizes away). Once words are formed it is a button that opens the
 * "This move" sheet: every word, its points and its multiplier breakdown. An
 * invalid move's reason may wrap to a second line.
 */
export function PreviewStrip({ preview, firstMove }: { preview: PreviewModel; firstMove: boolean }) {
  const expanded = useUiStore((s) => s.dialog === 'preview');
  const hintId = useId();
  const openDialog = useUiStore((s) => s.openDialog);
  const ref = useRef<HTMLElement | null>(null);
  const setRef = (el: HTMLElement | null) => {
    ref.current = el;
  };
  useRefusalNudge(() => ref.current);
  const { head, tail } =
    preview.state === 'empty'
      ? { head: firstMove ? 'Place tiles — first word must cover the ★' : 'Place tiles to see your score', tail: '' }
      : previewParts(preview);
  // The blank-letter prompt is a next step, not an error.
  const look = preview.state === 'invalid' && preview.tone === 'info' ? 'info' : preview.state;
  const content = (
    <>
      {look === 'valid' && <IconCheck />}
      {look === 'invalid' && <IconX />}
      {look === 'checking' && <span className="word-chip__dot" aria-hidden="true" />}
      <span className="preview-strip__text" title={head + tail}>
        {head}
      </span>
      {tail && <span className="preview-strip__tail tabular">{tail}</span>}
    </>
  );
  const className = clsx('preview-strip', `preview-strip--${look}`);
  if (preview.words.length === 0) {
    return (
      <p ref={setRef} className={className} data-coach="preview">
        {content}
      </p>
    );
  }
  return (
    <>
      <button
        ref={setRef}
        type="button"
        className={clsx(className, 'preview-strip--button')}
        data-coach="preview"
        aria-haspopup="dialog"
        aria-expanded={expanded}
        aria-describedby={hintId}
        onClick={() => openDialog('preview')}
      >
        {content}
        <IconChevronUp className="preview-strip__more" />
      </button>
      <span id={hintId} hidden>
        Shows every word with its points and multiplier breakdown.
      </span>
    </>
  );
}

/** Polite, throttled live region for the preview (settles before speaking). */
export function PreviewAnnouncer({ preview }: { preview: PreviewModel }) {
  const [text, setText] = useState('');
  const timer = useRef<number | undefined>(undefined);
  const line = preview.state === 'empty' || preview.state === 'checking' ? '' : previewLine(preview);
  useEffect(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setText(line), 900);
    return () => window.clearTimeout(timer.current);
  }, [line]);
  return (
    <div className="sr-only" aria-live="polite" aria-atomic="true">
      {text}
    </div>
  );
}
